/**
 * HeaderSocialChips — compact rank + pact chips rendered in the
 * dashboard header. Two atomic pieces (rank, pact) so we can compose
 * them or use either one independently.
 *
 * Both chips auto-hide when their underlying data is null (e.g. user
 * hasn't set an institution, or has no active pact). Hover surfaces a
 * subtle promo for accounts where the chip is hidden — but we don't
 * render that empty-state by default on the dashboard so the surface
 * stays clean for power users.
 */
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trophy, Handshake, ArrowRight } from 'lucide-react';
import { useSocialStrip } from '../../hooks/useSocialStrip';

export const UniversityRankChip = () => {
  const { data } = useSocialStrip();
  const lb = data?.leaderboard;
  if (!lb) return null;

  const isTop = lb.percentile >= 0.9;
  const tint = isTop
    ? 'bg-gradient-to-r from-amber-100 to-amber-200 dark:from-amber-900/40 dark:to-amber-800/40 border-amber-300 dark:border-amber-600/40 text-amber-800 dark:text-amber-200'
    : 'bg-[var(--isp-page)] border-[var(--f500-line)] text-[var(--f500-ink-1)]';

  return (
    <Link to="/leaderboard" data-testid="university-rank-chip">
      <motion.div
        initial={{ opacity: 0, x: -6 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2 }}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold hover:shadow-sm transition-shadow ${tint}`}
      >
        <Trophy className={`w-3.5 h-3.5 ${isTop ? 'text-amber-700' : 'text-[#002FA7]'}`} aria-hidden />
        <span>{lb.delta_label}</span>
        <ArrowRight className="w-3 h-3 opacity-50" aria-hidden />
      </motion.div>
    </Link>
  );
};

export const ActivePactChip = () => {
  const { data } = useSocialStrip();
  const pact = data?.active_pact;
  if (!pact) return null;

  const today = pact.you_checked_in_today;
  return (
    <Link to="/pacts" data-testid="active-pact-chip">
      <motion.div
        initial={{ opacity: 0, x: -6 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.25 }}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold hover:shadow-sm transition-shadow ${
          today
            ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
            : 'bg-orange-50 border-orange-200 text-orange-700'
        }`}
      >
        <Handshake className="w-3.5 h-3.5" aria-hidden />
        <span>
          {today ? '✓' : '⚠'} Pact w/ {pact.partner_name} · day {pact.streak_days}
        </span>
        <ArrowRight className="w-3 h-3 opacity-50" aria-hidden />
      </motion.div>
    </Link>
  );
};
